import { useCallback, useEffect, useState } from 'react'
import { KeyRound, ShieldAlert, ShieldCheck } from 'lucide-react'
import { adminMutate, usePermissions } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import {
  estCredentialPutResult, messageCodeTest, valeursCandidates,
} from './credential-model.js'
import {
  estListeSources, estResultatTest, estSourceView, fmtDateHeure, lireSupervision, messageEchecMutation, STATUT_SOURCE,
  type Lecture, type SondeSourceView, type SondeTestResult,
} from './api.js'

/**
 * PANNEAU « ACCÈS DE SONDE » — la clé d'un compte LECTEUR qu'utilise Sonde
 * pour lire un équipement (UniFi, NVR, iDRAC, Home Assistant…).
 *
 * Même doctrine que le panneau « Clé d'accès » de Gestion (CleAccesPanel) :
 *   · les champs sont TOUJOURS vides à l'ouverture : la clé ne redescend
 *     jamais (Sonde ne la renvoie pas, Gestion ne l'a pas) ;
 *   · « Tester » l'essaie depuis Sonde SANS l'enregistrer ;
 *   · « Enregistrer » demande la confirmation forte (adminMutate rejoue la
 *     requête après ré-authentification) ; Sonde teste encore avant de ranger ;
 *   · le statut affiché est celui de la DERNIÈRE LECTURE de Sonde, jamais
 *     celui d'un réglage : une clé enregistrée n'est pas une source qui lit.
 *
 * ÉCRITURE SEULE : Gestion relaie la clé à Sonde et n'en garde rien — ni au
 * coffre, ni en base, ni dans l'audit, ni dans un journal. Le panneau le dit.
 *
 * Boutons et pas Save Bar : remplacer une clé est un geste atomique et
 * sensible (doctrine de shell/editing.tsx), et un secret ne s'attarde pas.
 */

type Verdict = { ton: 'ok' | 'ko' | 'doute' | 'gris'; texte: string }

export function SondeCredentialPanel({ source, titre, intro }: {
  /** La vue de Sonde (page Intégrations › Sonde) ou son code (pages d'intégration). */
  source: SondeSourceView | string
  titre?: string
  intro?: string
}) {
  const code = typeof source === 'string' ? source : source.code
  const permissions = usePermissions()
  const peutModifier = permissions.permissions.includes('sonde.credentials.manage')
  const confirm = useConfirm()

  const [lecture, setLecture] = useState<Lecture<SondeSourceView> | null>(
    typeof source === 'string' ? null : { etat: 'ok', data: source })
  // Toujours vides au départ : la valeur ne vient JAMAIS du serveur.
  const [valeurs, setValeurs] = useState<Record<string, string>>({})
  const [essai, setEssai] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const [verdict, setVerdict] = useState<(Verdict & { pour: string }) | null>(null)
  const [apres, setApres] = useState<Verdict | null>(null)

  const relire = useCallback(() => {
    void lireSupervision('/sources', estListeSources).then((l) => {
      if (l.etat !== 'ok') { setLecture(l); return }
      const v = l.data.sources.find((s) => s.code === code)
      setLecture(v ? { etat: 'ok', data: v } : { etat: 'erreur', message: 'Sonde ne connaît pas cette source.', code: null })
    })
  }, [code])
  useEffect(() => { if (typeof source === 'string') relire() }, [source, relire])
  useEffect(() => { if (typeof source !== 'string') setLecture({ etat: 'ok', data: source }) }, [source])

  const titrePanneau = titre ?? (lecture?.etat === 'ok' ? lecture.data.label : 'Accès de Sonde')
  const entete = (
    <header className="lc-panel__head">
      <h2 className="lc-section-title" id={`sv-cle-${code}`}><KeyRound size={16} aria-hidden="true" /> {titrePanneau}</h2>
      {lecture?.etat === 'ok' ? (
        <span className={`lc-pill ${STATUT_SOURCE[lecture.data.status]?.pill ?? 'lc-pill--idle'}`}>
          {STATUT_SOURCE[lecture.data.status]?.texte ?? lecture.data.status}
        </span>
      ) : null}
    </header>
  )
  const piedGestion = (
    <p className="lc-hint sv-rien-garde">
      <ShieldCheck size={13} aria-hidden="true" /> Gestion ne garde rien : la clé part directement à Sonde, qui la range dans son
      coffre. Elle n’est enregistrée ni dans Gestion, ni dans son journal, ni dans l’audit.
    </p>
  )

  if (!lecture) return null
  if (lecture.etat !== 'ok') {
    return (
      <section className="lc-panel cle-panel" aria-labelledby={`sv-cle-${code}`}>
        {entete}
        <div className="lc-panel__body cle-corps">
          {intro ? <p className="lc-hint cle-intro">{intro}</p> : null}
          <p className="cle-verdict" role="status">
            {lecture.etat === 'non_raccordee'
              ? 'Sonde n’est pas encore raccordée : ce panneau s’ouvrira une fois Sonde branchée (Intégrations › Sonde).'
              : lecture.message}
          </p>
          {piedGestion}
        </div>
      </section>
    )
  }

  const s = lecture.data
  const champs = s.credential.fields
  const candidat = valeursCandidates(champs, valeurs)
  const empreinte = JSON.stringify(candidat.credential)
  const verdictCourant = verdict && verdict.pour === empreinte ? verdict : null
  const nvr = s.code.startsWith('hikvision_nvr')

  const tester = async () => {
    if (!candidat.complet) return
    setEssai(true); setApres(null)
    try {
      const r = await adminMutate<SondeTestResult & { error?: string; message?: string }>(
        'POST', `/api/v1/supervision/sources/${encodeURIComponent(s.code)}/test`, { credential: candidat.credential })
      if (r.status === 200 && estResultatTest(r.data)) {
        setVerdict({ ton: r.data.ok ? 'ok' : r.data.code === 'TEST_THROTTLED' ? 'doute' : 'ko', texte: r.data.ok ? `Clé acceptée${r.data.latency_ms !== null ? ` (réponse en ${Math.round(r.data.latency_ms)} ms)` : ''} — elle n’est pas encore enregistrée.` : messageCodeTest(r.data.code, r.data.message_fr), pour: empreinte })
      } else {
        setVerdict({ ton: 'gris', texte: messageEchecMutation(r.status, r.data, 'l’essai n’a pas eu lieu'), pour: empreinte })
      }
    } catch {
      setVerdict({ ton: 'gris', texte: 'Gestion n’a pas répondu : l’essai n’a pas eu lieu.', pour: empreinte })
    } finally { setEssai(false) }
  }

  const enregistrer = async () => {
    if (!candidat.complet) return
    if (verdictCourant?.ton !== 'ok') {
      const ok = await confirm({
        title: 'Enregistrer une clé qui n’a pas été acceptée ?',
        message: verdictCourant
          ? `Le dernier essai n’a pas abouti : ${verdictCourant.texte} Sonde la testera encore et refusera de l’enregistrer si l’équipement la rejette.`
          : 'Cette clé n’a pas été essayée. Sonde la testera avant de l’enregistrer ; mieux vaut cliquer d’abord sur « Tester ».',
        variant: 'sensitive',
        confirmLabel: 'Enregistrer quand même',
      })
      if (!ok) return
    }
    setEnvoi(true); setApres(null)
    try {
      const r = await adminMutate<{ error?: string; message?: string; details?: unknown }>(
        'PUT', `/api/v1/supervision/sources/${encodeURIComponent(s.code)}/credential`, { credential: candidat.credential })
      if (r.status >= 200 && r.status < 300) {
        // Vidés AVANT tout le reste : le secret ne séjourne pas dans la page.
        setValeurs({}); setVerdict(null)
        if (estCredentialPutResult(r.data)) {
          const res = r.data
          setLecture({ etat: 'ok', data: { ...s, credential: res.credential } })
          setApres(res.test.ok
            ? { ton: 'ok', texte: 'Clé enregistrée dans Sonde et acceptée par l’équipement. Le statut ci-dessus suivra la prochaine relève.' }
            : { ton: 'doute', texte: `Clé enregistrée dans Sonde, mais son essai n’a pas abouti : ${messageCodeTest(res.test.code, res.test.message_fr)}` })
        } else {
          setApres({ ton: 'ok', texte: 'Clé enregistrée dans Sonde.' })
        }
        window.setTimeout(relire, 4000)
        return
      }
      // Sonde a testé et refusé : rien n'est enregistré, l'ancienne clé reste.
      const det = (r.data?.details as { test?: unknown } | undefined)?.test
      if (r.data?.error === 'CREDENTIAL_TEST_FAILED' && estResultatTest(det)) {
        setApres({ ton: 'ko', texte: `Clé non enregistrée — ${messageCodeTest(det.code, det.message_fr)} L’ancienne clé de Sonde reste en service.` })
        return
      }
      setApres({ ton: r.status === 503 ? 'gris' : 'ko', texte: messageEchecMutation(r.status, r.data, 'la clé n’a pas été enregistrée') })
    } catch {
      setApres({ ton: 'gris', texte: 'Gestion n’a pas répondu : on ne sait pas si Sonde a reçu la clé. Relisez cette page avant de réessayer.' })
    } finally { setEnvoi(false) }
  }

  const faireConfiance = async () => {
    const fp = s.tls.pending_fingerprint
    if (!fp) return
    const ok = await confirm({
      title: 'Faire confiance au nouveau certificat ?',
      message: `Sonde a vu un certificat différent sur ${s.label}. Si l’équipement a été remplacé ou son certificat renouvelé, faites-lui confiance : Sonde reprendra la lecture. Sinon, quelqu’un pourrait se faire passer pour lui — ne confirmez pas.`,
      details: [{ label: 'Nouvelle empreinte', value: fp }],
      variant: 'critical',
      confirmLabel: 'Faire confiance',
    })
    if (!ok) return
    setEnvoi(true); setApres(null)
    try {
      const r = await adminMutate<{ error?: string; message?: string }>(
        'POST', `/api/v1/supervision/sources/${encodeURIComponent(s.code)}/trust-cert`, { fingerprint: fp })
      if (r.status >= 200 && r.status < 300) {
        if (estSourceView(r.data)) setLecture({ etat: 'ok', data: r.data })
        else relire()
        setApres({ ton: 'ok', texte: 'Nouveau certificat accepté : Sonde reprend la lecture.' })
        return
      }
      setApres({ ton: r.status === 503 ? 'gris' : 'ko', texte: messageEchecMutation(r.status, r.data) })
    } catch {
      setApres({ ton: 'gris', texte: 'Gestion n’a pas répondu.' })
    } finally { setEnvoi(false) }
  }

  const cred = s.credential
  return (
    <section className="lc-panel cle-panel" aria-labelledby={`sv-cle-${s.code}`}>
      {entete}
      <div className="lc-panel__body cle-corps">
        {intro ? <p className="lc-hint cle-intro">{intro}</p> : null}

        <dl className="cle-faits">
          <div>
            <dt>Clé de Sonde</dt>
            <dd>
              {!cred.configured ? 'Aucune clé enregistrée — Sonde ne lit pas cette source.'
                : `Enregistrée${cred.set_at ? ` le ${fmtDateHeure(cred.set_at)}` : ''}${cred.set_by ? `, par ${cred.set_by}` : ''}`}
            </dd>
          </div>
          {cred.configured ? (
            <div>
              <dt>Âge</dt>
              <dd>{cred.age_days === null ? '—' : cred.age_days === 0 ? 'posée aujourd’hui' : `${cred.age_days} jour${cred.age_days > 1 ? 's' : ''}`}</dd>
            </div>
          ) : null}
          <div>
            <dt>Dernier essai</dt>
            <dd>
              {cred.last_test_at === null ? '—'
                : `${fmtDateHeure(cred.last_test_at)} : ${cred.last_test_ok === null ? '—' : cred.last_test_ok ? 'réussi' : 'refusé'}${cred.last_test_detail ? ` (${cred.last_test_detail})` : ''}`}
            </dd>
          </div>
          <div>
            <dt>Dernière lecture réussie</dt>
            <dd>{fmtDateHeure(s.last_success_at)}</dd>
          </div>
          {s.status !== 'OK' && s.last_error_code ? (
            <div>
              <dt>Dernier refus</dt>
              <dd>{messageCodeTest(s.last_error_code, s.last_error_code)}{s.last_error_at ? ` — ${fmtDateHeure(s.last_error_at)}` : ''}</dd>
            </div>
          ) : null}
          {s.tls.mode === 'pin' ? (
            <div>
              <dt>Certificat épinglé</dt>
              <dd className="sv-mono">{s.tls.fingerprint ?? '—'}</dd>
            </div>
          ) : null}
        </dl>

        {s.tls.pending_fingerprint ? (
          <div className="cle-alerte" role="note">
            <ShieldAlert size={15} aria-hidden="true" />
            <span className="sv-pile">
              <span>Le certificat de l’équipement a changé. Sonde a suspendu la lecture en attendant votre décision.</span>
              <span className="sv-mono">{s.tls.pending_fingerprint}</span>
              {peutModifier ? (
                <span>
                  <button type="button" className="lc-btn" disabled={envoi} onClick={() => { void faireConfiance() }}>
                    Faire confiance au nouveau certificat
                  </button>
                </span>
              ) : null}
            </span>
          </div>
        ) : null}

        {nvr ? (
          <p className="cle-alerte" role="note">
            <ShieldAlert size={15} aria-hidden="true" />
            <span>Ne réessayez pas plus de deux fois une clé refusée : le NVR bloque l’adresse de Sonde vers 5 échecs.</span>
          </p>
        ) : null}

        {peutModifier && champs.length > 0 ? (
          <div className="cle-saisie">
            {champs.map((f) => (
              <label key={f.name} className="cle-champ">
                <span>{f.label}{f.required ? '' : ' (facultatif)'}</span>
                <input
                  className="lc-input" type={f.secret ? 'password' : 'text'}
                  autoComplete={f.secret ? 'new-password' : 'off'} spellCheck={false}
                  inputMode={f.type === 'integer' ? 'numeric' : undefined}
                  name={`sonde-${s.code}-${f.name}`} value={valeurs[f.name] ?? ''}
                  aria-label={`${f.label} — ${s.label}`}
                  onChange={(e) => { setValeurs((v) => ({ ...v, [f.name]: e.target.value })); setApres(null) }}
                />
              </label>
            ))}
            <div className="cle-actions">
              <button type="button" className="lc-btn" onClick={() => { void tester() }}
                      disabled={!candidat.complet || essai || envoi}>
                {essai ? 'Essai en cours…' : 'Tester'}
              </button>
              <button type="button" className="lc-btn lc-btn--primary" onClick={() => { void enregistrer() }}
                      disabled={!candidat.complet || essai || envoi}>
                {envoi ? 'Enregistrement…' : 'Enregistrer dans Sonde'}
              </button>
            </div>
            {verdictCourant ? (
              <p role="status" className={`cle-verdict${verdictCourant.ton === 'gris' ? '' : ` cle-verdict--${verdictCourant.ton}`}`}>{verdictCourant.texte}</p>
            ) : null}
          </div>
        ) : !peutModifier ? (
          <p className="lc-hint">Seul un compte autorisé à gérer les clés de Sonde peut la remplacer.</p>
        ) : null}
        {apres ? <p role="status" className={`cle-verdict${apres.ton === 'gris' ? '' : ` cle-verdict--${apres.ton}`}`}>{apres.texte}</p> : null}
        {piedGestion}
      </div>
    </section>
  )
}
